import { Label } from '@trucktrax/trucktrax-common';
import { DeviceDto, PermissionAccess, RegionDto, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import {
  ADMIN_DEVICES_PATH,
  ADMIN_TRUCKS_PATH,
  DEVICES_PATH,
  TRUCKS_PATH,
} from '../../../constants/apiConstants';
import {
  ACCENT,
  ACTIVATE_GLINX_BODY1,
  ADMIN_KEYS,
  ADMIN_LABELS,
  ARE_YOU_SURE,
  CANCEL_LABEL,
  CONFIRM_ACTIVATE_DEVICE,
  CONFIRM_DEACTIVATE_DEVICE,
  CUSTOM_COMPONENT,
  DEACTIVATE_GLINX_BODY1,
  DEACTIVATE_GLINX_BODY2,
  DEACTIVATE_GLINX_POINT1,
  DEACTIVATE_GLINX_POINT2,
  DEACTIVATE_GLINX_POINT3,
  GEN7,
  NOT_AVAILABLE,
  TEXT_DISPLAY,
} from '../../../constants/appConstants';
import { DEVICES_TEXT } from '../../../constants/navConstants';
import { updateDataTableRecord } from '../../../services/dataTableService';
import { getRequest } from '../../../services/requestService';
import { closeModal, openModal } from '../../../store/actions/errorModalActions';
import {
  exactFieldLength,
  getDeviceTypeLabel,
  getRegionUrlToNameMap,
  getUrl,
} from '../../../util/adminUtil';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import { getIdFromUrl, glinxGeneration, noop } from '../../../util/appUtil';
import AdminCard from '../../shared/admin/AdminCard';
import StatusContainer from '../status/StatusContainer';
import styles from './DeviceDetailView.module.css';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import { activeAndDeactivatedRegionsSelector } from '../../../store/selectors/regionSelectors';

interface NavLink {
  url: string;
  label: string;
}

interface DeviceDetailViewGLinxState {
  edit: boolean;
  deviceDetails: {
    archived: boolean;
    externalId: string;
  };
  truck: NavLink | null;
  tablet: NavLink | null;
}

export class DeviceDetailViewGLinx extends Component<
  DeviceDetailViewGLinxProps,
  DeviceDetailViewGLinxState
> {
  static defaultProps = {
    currentRegion: {
      url: '',
    },
  };

  state = {
    edit: false,
    deviceDetails: {
      archived: false,
      externalId: '',
    },
    truck: null,
    tablet: null,
  };

  toggleEdit = () => {
    this.setState({ edit: !this.state.edit });
  };

  baseUrl = () =>
    `${getGeotraxBaseUrl() + DEVICES_PATH}/${this.props.match.params.id.replace('devices/glinx', 'devices')}`;

  regionUrlAccessor = (row: DeviceDto) => getRegionUrlToNameMap(this.props.regions)[getUrl(row)!];

  static firmwareVersionAccessor = (row: DeviceDto) => {
    const firmwareData = row.extraInfo?.extraInfo;
    const isGen7 = glinxGeneration(firmwareData) === GEN7;

    if (isGen7) {
      return firmwareData?.BP ?? NOT_AVAILABLE;
    }
    return NOT_AVAILABLE;
  };

  static hardwareVersionAccessor = (row: DeviceDto) => {
    const firmwareData = row.extraInfo?.extraInfo;
    return firmwareData?.FW ?? NOT_AVAILABLE;
  };

  save = (dto: DeviceDto, onSuccess: any, onError: any, toastMessages: any) => {
    const onSaveSuccess = (updatedDto: { data: DeviceDto }) => {
      const deviceDetails = updatedDto && updatedDto.data ? updatedDto.data : {};
      this.setState({
        edit: false,
        deviceDetails,
      });
      onSuccess(updatedDto);
    };
    const currentRegionUrl = this.props.currentRegion.url;
    this.props.updateAdminTableData(
      this.baseUrl(),
      dto,
      onSaveSuccess,
      onError,
      false,
      currentRegionUrl,
      toastMessages,
    );
  };

  static getTruckById = async (truckBaseUrl: string, params: any) => {
    const responseTruck = await getRequest(truckBaseUrl, params);
    return responseTruck ? responseTruck.data : {};
  };

  handleCallback = async (response: { data: any }) => {
    const { data } = response;
    const params = {
      tenant: data.tenant?.url,
    };

    const truckId = getIdFromUrl(data.truck?.url);
    if (!truckId) {
      this.updateDeviceData({ tablet: null, truck: null }, data);
      return;
    }
    const truckRequestUrl = `${getGeotraxBaseUrl()}${TRUCKS_PATH}/${truckId}`;

    let truckResponse;
    try {
      truckResponse = await DeviceDetailViewGLinx.getTruckById(truckRequestUrl, params);
    } catch (e) {
      const deviceId = this.props.match?.params?.id ?? '';
      recordLog(
        LogLevel.Warn,
        `Failed to retrieve the truck associated with device ${deviceId}:`,
        e,
      );
    }

    if (!truckResponse) {
      this.updateDeviceData({ tablet: null, truck: null }, data);
      return;
    }

    const {
      associatedTablet: { id: tabletId, name: tabletName } = { id: '', name: '' },
      truckAlias = '',
    } = truckResponse;
    const truck: NavLink = {
      label: truckAlias,
      url: `${ADMIN_TRUCKS_PATH}/${truckId}`,
    };

    const tablet: NavLink = {
      url: tabletId ? `${ADMIN_DEVICES_PATH}/${tabletId}` : '',
      label: tabletId ? tabletName : '',
    };

    this.updateDeviceData({ tablet, truck }, data);
  };

  updateDeviceData = (info: Pick<DeviceDetailViewGLinxState, 'tablet' | 'truck'>, data: any) => {
    this.setState({ tablet: info.tablet, truck: info.truck });
    data.truckAlias = info.truck?.label ?? '';
    data.deviceType = 'GLINX';
    this.setData(data);
  };

  handleAlterData = (data: DeviceDto) => {
    this.setData(data);
    return data;
  };

  setData = (data: DeviceDto) => {
    this.setState({
      deviceDetails: { ...data },
    });
  };

  static checkLength = (value: any) => exactFieldLength(value, 15, ADMIN_LABELS.IMEINUM);

  getViewConfigWithoutEdit = () => {
    const { truck, tablet } = this.state;
    return [
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.NAME,
        accessor: ADMIN_KEYS.NAME,
        className: cx(styles.deviceNameGLinx, 'large'),
        dataTest: 'devices-drilldown-deviceType',
      },
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.DEVICE_TYPE,
        accessor: (row: { deviceType: any }) => getDeviceTypeLabel(row.deviceType),
        className: styles.deviceTypeGLinx,
        dataTest: 'devices-drilldown-deviceType',
      },
      {
        type: CUSTOM_COMPONENT,
        key: ADMIN_LABELS.LAST_ASSIGNED_TRUCK,
        className: styles.truckAlias,
        component: () => DeviceDetailViewGLinx.createLink(truck, ADMIN_LABELS.LAST_ASSIGNED_TRUCK),
      },
      {
        type: CUSTOM_COMPONENT,
        key: ADMIN_LABELS.LAST_ASSIGNED_TABLET,
        className: styles.truckAlias,
        component: () =>
          DeviceDetailViewGLinx.createLink(tablet, ADMIN_LABELS.LAST_ASSIGNED_TABLET),
      },
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.EXTERNAL_ID,
        accessor: ADMIN_KEYS.EXTERNAL_ID,
        className: styles.externalIdGlinx,
        dataTest: 'devices-drilldown-external-id',
      },
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.FIRMWARE_VERSION,
        accessor: DeviceDetailViewGLinx.firmwareVersionAccessor,
        className: styles.firmware,
        dataTest: 'devices-drilldown-android',
      },
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.HARDWARE_VERSION,
        accessor: DeviceDetailViewGLinx.hardwareVersionAccessor,
        className: styles.hardware,
        dataTest: 'devices-drilldown-model',
      },
      {
        type: TEXT_DISPLAY,
        label: ADMIN_LABELS.REGION,
        accessor: this.regionUrlAccessor,
        className: styles.regionGlinx,
        dataTest: 'devices-drilldown-region',
      },
    ];
  };

  static createLink = (link: NavLink | null, label: string | null | undefined) => {
    const { label: linkLabel = '', url = '' } = link ?? {};
    const hasNoUrlButHasLabel = linkLabel && !url;
    const includeNAAttribute = linkLabel ? '' : NOT_AVAILABLE;
    let linkElementContent: JSX.Element | string = NOT_AVAILABLE;

    if (hasNoUrlButHasLabel) {
      linkElementContent = linkLabel;
    } else if (linkLabel && url) {
      linkElementContent = (
        <Link className={styles.deviceLink} to={url}>
          {linkLabel}
        </Link>
      );
    }

    return (
      <div className={styles.truckAlias}>
        <Label className={styles.truckAliasLabel}>{label}</Label>
        <p title={includeNAAttribute}>{linkElementContent}</p>
      </div>
    );
  };

  viewOnlyItems() {
    if (!this.state.edit) {
      const hasEditPermission = this.props.devicesPermissionAccess === PermissionAccess.Edit;
      return (
        <StatusContainer
          category="device"
          isArchived={this.state.deviceDetails.archived}
          hasEditPermission={hasEditPermission}
          containerClassName={styles.statusCtr}
          onActivateClick={this.activateWarningModal}
          onDeactivateClick={this.deactivateWarningModal}
        />
      );
    }
    return null;
  }

  activateWarningModal = () => {
    const modalBody = (
      <span>
        <p className={styles.glinxModalText}>{ACTIVATE_GLINX_BODY1}</p>
      </span>
    );
    this.props.openModal({
      modalType: ACCENT,
      modalTitle: ARE_YOU_SURE,
      modalBody,
      modalOpen: true,
      acceptDialog: this.activateDevice,
      acceptText: CONFIRM_ACTIVATE_DEVICE,
      cancelText: CANCEL_LABEL,
      disabled: false,
      noActions: false,
    });
  };

  deactivateWarningModal = () => {
    const modalBody = (
      <span>
        <p>{DEACTIVATE_GLINX_BODY1}</p>
        <p>
          <ul>
            <li>{DEACTIVATE_GLINX_POINT1}</li>
            <li>{DEACTIVATE_GLINX_POINT2}</li>
            <li>{DEACTIVATE_GLINX_POINT3}</li>
          </ul>
        </p>
        <p className={styles.glinxModalText}>{DEACTIVATE_GLINX_BODY2}</p>
      </span>
    );

    this.props.openModal({
      modalType: ACCENT,
      modalTitle: ARE_YOU_SURE,
      modalBody,
      modalOpen: true,
      acceptDialog: this.deactivateDevice,
      acceptText: CONFIRM_DEACTIVATE_DEVICE,
      cancelText: CANCEL_LABEL,
      disabled: false,
      noActions: false,
    });
  };

  deactivateDevice = () => {
    const dto = {
      ...this.state.deviceDetails,
      archived: true,
    } as DeviceDto;

    const toastMessages = {
      success: `Device “${dto.externalId}” has been successfully deactivated.`,
      fail: `Unable to deactivate “${dto.externalId}.”`,
    };

    this.props.closeModal();
    this.save(dto, noop, null, toastMessages);
  };

  activateDevice = () => {
    const dto = {
      ...this.state.deviceDetails,
      archived: false,
    } as DeviceDto;
    const toastMessages = {
      success: `Device "${dto.externalId}" has been successfully activated.`,
      fail: `Unable to activate “${dto.externalId}.”`,
    };

    this.props.closeModal();
    this.save(dto, noop, null, toastMessages);
  };

  getBreadcrumbPath() {
    if (!this.state.deviceDetails.archived) {
      return this.props.location.pathname.replace('/glinx', '');
    }
    // construct the breadcrumb path for deactivated devices
    const paths = this.props.location.pathname.replace('/glinx', '').split('/');
    const id = paths.pop();
    if (id) {
      paths.push('deactivated', id);
    }
    return paths.join('/');
  }

  getConfigValue() {
    const viewConfigWithoutEdit = this.getViewConfigWithoutEdit();
    return viewConfigWithoutEdit;
  }

  render() {
    const pathname = this.getBreadcrumbPath();
    const configValue = this.getConfigValue();

    return (
      <AdminCard
        edit={this.state.edit}
        url={this.baseUrl()}
        className={cx(styles.wrapper, this.state.edit && styles.editWrapper)}
        onToggleEdit={this.toggleEdit}
        save={this.save}
        config={configValue}
        pathName={pathname}
        headerAccessor="name"
        options={{
          callback: this.handleCallback,
          alterData: this.handleAlterData,
          requiredKeys: [],
        }}
      >
        {this.viewOnlyItems()}
      </AdminCard>
    );
  }
}

const mapStateToProps = (state: any) => ({
  regions: activeAndDeactivatedRegionsSelector(state),
  devicesPermissionAccess: state.adminPermissionAccess[DEVICES_TEXT],
  currentRegion: state.currentRegion,
});

type Location = {
  pathname: string;
};
type Match = {
  params: { id: string };
};
export interface DeviceDetailViewGLinxProps {
  updateAdminTableData: typeof updateDataTableRecord;
  match: Match;
  closeModal: typeof closeModal;
  openModal: typeof openModal;
  devicesPermissionAccess?: string;
  regions: RegionDto[];
  currentRegion: UrlKeyDto;
  location: Location;
}
export default connect<any, any, any>(mapStateToProps, {
  updateAdminTableData: updateDataTableRecord,
  openModal,
  closeModal,
})(DeviceDetailViewGLinx);
